/**
 * Moving a running session to another account.
 *
 * It is a restart, and the dialog says so before it asks for anything. The
 * credential a CLI uses is the directory named in its process environment, fixed
 * at exec — nothing can change the account of a running process, so continuing
 * on another one means stopping this session and starting a new one.
 *
 * And the conversation does not come with it. Each account keeps its history
 * under its own configuration root, so the new session opens on an empty one.
 * That is the consequence an operator would otherwise discover afterwards, which
 * is why it is the largest thing on this dialog rather than a footnote: this is
 * the point of no return for a thread that may have taken hours.
 */

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { api, type PageResponse } from '@/api/client'
import { Button, Badge } from '@/components/primitives'
import { Dialog, SelectField, Notice, useApiErrorMessage } from '@/components/controls'
import { useT } from '@/i18n/context'
import { accountReachableFrom, type ReachableHost } from '@/lib/accountReach'
import type { HandoffDecision, ID } from '@/types/domain'

interface AccountRow {
  id: ID
  label: string
  provider: string
  enabled: boolean
  hostId?: ID
  hostName?: string
  /** The directory is on storage every host mounts (ADR-0013). Without this the
   *  filter below hid every shared account and told an operator the work had
   *  nowhere to go. */
  sharedStorage?: boolean
}

export function SwitchAccountDialog({
  sessionId,
  sessionName,
  provider,
  host,
  hostName,
  currentAccountId,
  currentAccountLabel,
  initialTarget,
  onClose,
  onSwitched,
}: {
  sessionId: string
  sessionName: string
  provider: string
  host: ReachableHost | undefined
  hostName?: string
  currentAccountId?: ID
  currentAccountLabel?: string
  /** A suggested target — the conductor's, when the dialog was opened from a
   *  recommendation. A prefill and nothing more: it seeds the field once, is
   *  offered only if the account is one this dialog would list anyway, and the
   *  operator's own choice is what the request carries. */
  initialTarget?: ID
  onClose: () => void
  onSwitched: (id: string) => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  const [target, setTarget] = useState<string>(initialTarget ?? '')

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<AccountRow>>('/accounts'),
  })

  // The router's read of why a move is warranted and where the work would go
  // (ADR-0020). A POST that computes without moving anything; shown, never
  // auto-applied — the operator still picks, and the switch carries the context.
  const advice = useQuery({
    queryKey: ['handoff', sessionId],
    queryFn: () => api.post<HandoffDecision>(`/sessions/${sessionId}/handoff`, {}),
  })
  const failure = advice.data?.failure

  // The same three rules the server applies, so the list offers nothing it would
  // then refuse: the provider has to match (a claude session cannot continue on
  // a codex account), the account has to be enabled, and its credentials have to
  // be readable from the host this session runs on.
  //
  // That last one is `accountReachableFrom`, not an equality here. Written as
  // one it was wrong: it compared host ids alone, which hides every account on
  // shared storage — and on this fleet that is all of them, so the dialog told
  // an operator there was nowhere for the work to go while every account was
  // reachable from every host.
  const usable = (accounts.data?.items ?? []).filter(
    (a) =>
      a.id !== currentAccountId &&
      a.enabled &&
      a.provider === provider &&
      accountReachableFrom(a, host),
  )

  const chosen = usable.some((a) => a.id === target) ? target : ''
  const move = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>(`/sessions/${sessionId}/account`, { accountProfileId: chosen }),
    onSuccess: (started) => onSwitched(started.id),
  })

  return (
    <Dialog
      title={t('live.switchAccount')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            busy={move.isPending}
            disabled={!chosen}
            onClick={() => move.mutate()}
          >
            {t('live.switchConfirm')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <div className="k-switch__from">
          <span className="k-switch__name">{sessionName}</span>
          <Badge tone="neutral" subtle>
            {provider}
          </Badge>
          {currentAccountLabel && (
            <span className="k-switch__acct k-mono">{currentAccountLabel}</span>
          )}
        </div>

        {failure?.justifiesReroute && (
          <Notice tone="warn">
            {t('live.handoff.failure', { reason: failure.reason })}
            {advice.data?.recommendedAccountLabel
              ? ' ' + t('live.handoff.recommend', { account: advice.data.recommendedAccountLabel })
              : ''}
          </Notice>
        )}

        <SelectField
          label={t('live.switchTo')}
          hint={t('live.switchToHint', { host: hostName ?? '' })}
          // A seeded target that the rules above would not list is shown as no
          // choice at all, so the suggestion can never reach the server ahead of
          // the same refusal the dialog exists to apply first.
          value={usable.some((a) => a.id === target) ? target : ''}
          onChange={setTarget}
          options={[
            { value: '', label: t('live.switchPick') },
            ...usable.map((a) => ({ value: a.id, label: `${a.label} · ${a.provider}` })),
          ]}
        />

        {accounts.isSuccess && usable.length === 0 && (
          // Said as a fact about this host, not as an empty dropdown: the reason
          // the list is empty is the one thing that would tell an operator what
          // to do about it.
          <Notice tone="warn">{t('live.switchNoneHere', { host: hostName ?? '' })}</Notice>
        )}

        {/* Before the button, not after the act. */}
        <Notice tone="warn">{t('live.switchWarning')}</Notice>
        <p className="k-switch__handoff">{t('live.handoff.carries')}</p>

        {move.error && <Notice tone="err">{describe(move.error).title}</Notice>}
      </div>
    </Dialog>
  )
}
